import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StepData, Token } from 'src/app/app.model';
import { AppService } from 'src/app/app.service';
import { CacheService } from 'src/app/cache.service';
import { environment } from 'src/environments/environment';
import { MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig } from '@angular/material/legacy-snack-bar';
import { first, get, includes, trim, some as _some, toPairs as _toPairs, intersection as _intersection } from 'lodash-es';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, map, skipWhile } from 'rxjs/operators';
import { composeRoleAndFeatureFlagsRules, operators } from 'src/app/shared/helpers/helpers';
import * as jsonLogic from 'json-logic-js';
import { DataService } from 'src/app/shared/services/data/data.service';
import { TemplateSourceService } from 'src/app/shared/services/source-service-api/template-source.service';

export const exists = (data, rule) => {
  return data.every(val => rule.includes(val));
}
jsonLogic.add_operation('exists', exists)

export const intersection = (data, rule) => {
  return _intersection(data, rule).length > 0;
}
jsonLogic.add_operation('intersection', intersection)


@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  public dataService: DataService;
  private insightsApiUrl = environment.insightsApiUrl;
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  private params = new HttpParams();
  private appUrl = environment.apiUrl;
  private apiUrlSourceService = environment.apiUrlSourceService;
  
  private templateFeatureFlags: any;
  private _templateFeatureFlags$ = new BehaviorSubject<any>({
    'saras': 'No',
    'company': 'No',
  });
  public possibleRoute;
  public ALLOWED_TEMPLATE_ROLES = environment.ALLOWED_TEMPLATE_ROLES;

  public RESTRICTED_TEMPLATE_PLANS = environment.RESTRICTED_TEMPLATE_PLANS;

  public ALLOWED_TEMPLATE_PLANS = environment.ALLOWED_TEMPLATE_PLANS;

  public ROLES_FOR_ORG_TEMPLATES = [];

  public ROLES_FOR_SARAS_TEMPLATES = ['TemplateAdmin'];

  public readonly templateFeatureFlags$: Observable<any> = this._templateFeatureFlags$.asObservable()
    .pipe(skipWhile(data => data === undefined || data === null));

  constructor(private appService: AppService, private cacheService: CacheService,
    private http: HttpClient, private snackBarService: MatSnackBar, dataService: DataService,
    private templateSourceApi: TemplateSourceService) {
    this.dataService = dataService;
  }

  private setAuthHeader(): HttpHeaders {
    return this.headers.set(
      'Authorization',
      'Bearer ' + this.cacheService.getObject<Token>('saras_auth_key').token
    );
  }

  async getStudioInit(userId, sourceName, templateId, isLegacy: boolean = false){
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    if (isLegacy) {
      return this.http
      .get<any>(`${this.appUrl}integration-setup/init-flow-with-template-and-get-first-step/${userId}/${sourceName}/${templateId}`, {
        headers: headers,
      }).toPromise()
      .catch((error) => this.appService.handleErrorDontRedirect(error));
    } else {
      return this.http
      .get<any>(`${this.apiUrlSourceService}/integration-setup/init-flow-with-template-and-get-first-step/userId/${userId}/dataSourceType/${sourceName}/templateId/${templateId}`, {
        headers: headers,
      }).toPromise()
      .catch((error) => this.appService.handleErrorDontRedirect(error));
    }
  }

  async getTemplate(userId: number, templateId: number): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
    .get<any>(`${this.appUrl}template/${userId}/${templateId}/created`, {
      headers: headers,
    }).toPromise()
    .catch((error) => this.appService.handleErrorDontRedirect(error));

  }

  async getConfiguredTemplate(userId: number, templateId: number): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();
    return this.http
    .get<any>(`${this.appUrl}template/${userId}/${templateId}/configured`, {
      headers: headers,
    })
    .toPromise()
    .catch((error) => this.appService.handleErrorDontRedirect(error));
  }

  async getTemplateList(userId: number, isLegacy: boolean = false): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    if (isLegacy) {
      return this.http
        .get<any>(`${this.appUrl}template/${userId}/active`, {
          headers: headers,
        })
        .toPromise()
        .catch((error) => this.appService.handleErrorDontRedirect(error));
    } else {
      return this.templateSourceApi.getActiveTemplateList(userId).toPromise()
        .catch((error) => this.appService.handleErrorDontRedirect(error));
    }
  }

  async saveTemplateInputAttributes(userId: number, sourceId: number, data: StepData[], type: string, ifAuthDone): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
    .post<any>(`${this.appUrl}source/${sourceId}/${userId}/save-values/${type}/${ifAuthDone}`,
      data,
      {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.appService.handleErrorDontRedirect(err));
  }

  async createTemplate(userId: number, sourceId: number, data: StepData[]): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .put<any>(`${this.appUrl}template/${userId}/${sourceId}/create`, data , {
        headers: headers
      })
      .toPromise()
      .catch((error) => this.appService.handleErrorDontRedirect(error));

  }

  async setTemplateStepData(templateId: number, data: StepData[]): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .post<any>(`${this.appUrl}template/${templateId}/step`, data, {
        headers: headers,
        observe: 'response'
      })
      .toPromise()
      .catch((error) => {
        console.log(error);
        this.templatesStepErrorHandler(error);
      });
  }

  async setSourceTemplateStepData(templateId: number, sourceId: number, data: StepData[]): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .post<any>(`${this.appUrl}template/${templateId}/${sourceId}/step`, data, {
        headers: headers,
        observe: 'response'
      })
      .toPromise()
      .catch((error) => {
        this.templatesStepErrorHandler(error);
        return Promise.reject(error);
      });
  }

  async saveTemplateData(templateId: number, data: StepData[]): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .put<any>(`${this.appUrl}template/${templateId}/save`, data, {
        headers: headers,
      })
      .toPromise()
      .catch((error) => this.appService.handleErrorDontRedirect(error));
  }

  async deleteTemplate(userId: number, templateId: number, templateData?: any): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();   
    const isLegacy = templateData?.isLegacy ?? true;   
    if (isLegacy) {
      return this.http
        .get(`${this.appUrl}template/${userId}/${templateId}/delete`, {
          headers: headers,
        }).toPromise()
        .catch((error) => this.appService.handleErrorDontRedirect(error));
    } else {
      return this.templateSourceApi.deleteTemplate(templateId, userId).toPromise()
        .catch((error) => this.appService.handleErrorDontRedirect(error));
    }
  }


  async getAttachSourceSteps(userId: number, templateId: number): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
    .get<any>(`${this.appUrl}template/${userId}/${templateId}/addSource`, {
      headers: headers,
    }).toPromise().catch((error) => this.appService.handleErrorDontRedirect(error));
  }

  async saveSourceTemplateData(templateId: number, sourceId: number, data: StepData[]): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .post<any>(`${this.appUrl}template/${templateId}/${sourceId}/save`, data, {
        headers: headers,
      }).toPromise().catch((error) => this.appService.handleErrorDontRedirect(error));
  }

  async deLinkSourceFromTemplate(templateId: number, sourceId: number, isLegacy: boolean = true): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    if (isLegacy) {
      return this.http
        .get<any>(`${this.appUrl}template/${templateId}/${sourceId}/delink`, {
          headers: headers,
        }).toPromise().catch((error) => this.appService.handleErrorDontRedirect(error));
    } else {
      return this.templateSourceApi.delinkSourceFromTemplate(templateId, sourceId).toPromise()
        .catch((error) => this.appService.handleErrorDontRedirect(error));
    }
  }

  async getSavedTemplateSettingPrimaryFields(templateId: number): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .get<any>(`${this.appUrl}template/${templateId}/savedSettings`, {
        headers: headers,
      })
      .toPromise()
      .catch((error) => this.appService.handleErrorDontRedirect(error));
  }

  async applyTemplateChangesNow(
    templateId: number,
    data: StepData[],
    reloadAttributes: string[],
    userId: number,
    changedTables: any
  ): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .post<any>(`${this.appUrl}template/${templateId}/apply`, {
        prevSteps: data,
        modifiedTables: reloadAttributes,
        changedTables: changedTables,
      }, {
        headers: headers,
      })
      .toPromise()
      .catch((error) => this.appService.handleErrorDontRedirect(error));
  }

  async bulkApplyTemplateChanges(
    templateId: number,
    sourceIDs: number[],
    isLegacy: boolean = true
  ): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();
    if (isLegacy) {
      return this.http
        .post<any>(`${this.appUrl}template/${templateId}/applyChanges`, sourceIDs, {
          headers: headers
        })
        .toPromise()
        .catch((error) => this.appService.handleErrorDontRedirect(error));
    } else {
      const userId = this.appService.userData.userId;
      return this.templateSourceApi.bulkApplyTemplateChanges(templateId, sourceIDs, userId).toPromise()
        .catch((error) => this.appService.handleErrorDontRedirect(error));
    }
  }

  async bulkDelinkIntegrationsFromTemplate(userId: number, templateId: number, sourceIDs: number[], isLegacy: boolean = true): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    if (isLegacy) {
      return this.http
        .post<any>(`${this.appUrl}template/${userId}/${templateId}/bulkdelink`, sourceIDs, {
          headers
        })
        .toPromise()
        .catch((error) => this.appService.handleErrorDontRedirect(error));
    } else {
      return this.templateSourceApi.bulkDelinkIntegrationsFromTemplate(templateId, userId, sourceIDs).toPromise()
        .catch((error) => this.appService.handleErrorDontRedirect(error));
    }
  }

  async applyTemplateChangesLater(
    templateId: number,
    data: StepData[],
    reloadAttributes: string[],
    userId: number,
    changedTables: any
  ): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .post<any>(`${this.appUrl}template/${templateId}/later`,
      {
        prevSteps: data,
        modifiedTables: reloadAttributes,
        changedTables: changedTables,
      }, {
        headers: headers,
      })
      .toPromise()
      .catch((error) => this.appService.handleErrorDontRedirect(error));
  }

  async saveEditedTemplateStepsData(
    templateId: number,
    data: StepData[],
    reloadAttributes: string[],
    userId: number,
    changedTables: any
  ): Promise<any> {

    await this.appService.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(
        this.appUrl + 'template/' + templateId + '/' + userId + '/edit',
        {
          prevSteps: data,
          modifiedTables: reloadAttributes,
          changedTables: changedTables,
        },
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.appService.handleErrorDontRedirect(err));
  }

  async getOrgTemplateNames(userId: number): Promise<string[]> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .get<string[]>(`${this.appUrl}template/${userId}/OrgTemplatesNames`, {
        headers: headers,
      })
      .toPromise()
      .catch((error) => this.appService.handleError(error));
  }

  templatesStepErrorHandler(error) {
    const errorMessage = get(first(get(error, 'error.errors')), 'message', '');
    const trimMessage = trim(errorMessage.replace('com.saras.webapp.StepException:', ''));
    return this.snackBarService.open(trimMessage, '', this.getSnackBarConfig());
  }


  getSnackBarConfig(): MatSnackBarConfig  {
    return  {
      duration: 2500,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: ['reload-snack-bar']
    };
  }

  async getOrgTemplates(userId: number, sourceId: number): Promise<any> {
    await this.appService.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .get<any>(`${this.appUrl}template/${userId}/${sourceId}/OrgTemplates`, {
        headers
      }).toPromise()
      .catch((error) => this.appService.handleError(error));
  }

  checkTemplateFeaturePermission(userRole, userPlanId) {
    // if(includes(this.ALLOWED_TEMPLATE_ROLES, userRole)) {
    //   return true
    // } else if(includes(this.ALLOWED_TEMPLATE_PLANS, userPlanId) && environment.allowTemplatesFeatureOnlyForTemplateAdmin === false) {
    //   return true;
    // } else if(includes(this.RESTRICTED_TEMPLATE_PLANS, userPlanId)) {
    //   return false;
    // } else {
    //   return false
    // }

    return from(this.getTemplateList(this.appService.userData.userId));
  }

  async  getActiveIntegrations(templateId) {
    await this.appService.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
    .get<any>(`${this.appUrl}template/${templateId}/activeIntegrations`, {
      headers
    }).toPromise()
    .catch((error) => this.appService.handleError(error));
  }

  public set setTemplateFeatureFlags(v: any) {
    this._templateFeatureFlags$.next(v);
    this.templateFeatureFlags = v;
  }

  public get getTemplateFeatureFlags(): any {
    return this.templateFeatureFlags;
  }

  public computeValue(data, flags, role?, operator?) {
    const featureFlagKeyValue = _toPairs(flags)
    const featureFlagRule = operators[operator](featureFlagKeyValue)
    if(role) {
      // const roleRule = operators['role'](role) // TODO: Commenting out this line for RBAC based feature flags implementation
      const roleRule = operators['roleIntersection'](role)
      const composedRule = composeRoleAndFeatureFlagsRules(featureFlagRule, roleRule)
      return jsonLogic.apply(composedRule, {...data.templateFeatureFlags, role: data.role});
    } else {
      return jsonLogic.apply(featureFlagRule, {...data.templateFeatureFlags});
    }
  }

  public getInsightsTemplatesStatus() {
    const option = {
      url: `${this.insightsApiUrl}/template/status`
    }
    return this.dataService.get(option)
  }

  // New Template Setup APIs - only used when isLegacy is false
  async initSetupFlowAndGetFirstStep(userId: number, dataSourceType: string, isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      return this.getTemplateList(userId);
    }
    
    return this.templateSourceApi.initTemplateSetupFlow(userId, dataSourceType).toPromise();
  }

  async initEditFlowAndGetFirstStep(userId: number, dataSourceType: string, templateId: number, isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      return this.getTemplate(userId, templateId);
    }
    
    return this.templateSourceApi.initTemplateEditFlow(templateId, userId, dataSourceType).toPromise();
  }

  async getNextStep(sessionState: string, currentStepUserInput: any, isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      return Promise.resolve({});
    }
    
    return this.templateSourceApi.getTemplateNextStep(sessionState, currentStepUserInput).toPromise();
  }

  async getPreviousStep(sessionState: string, isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      return Promise.resolve({});
    }
    
    return this.templateSourceApi.getTemplatePreviousStep(sessionState).toPromise();
  }

  async saveConnectorStudioTemplate(userId: number, dataSourceId: number, dataSourceType: string, 
    templateData: { warehouseId?: number, destinationType?: string, templateId?: string, sessionState: string }, 
    isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      return this.createTemplate(userId, dataSourceId, []);
    }
    
    return this.templateSourceApi.saveConnectorStudioTemplate(templateData, userId, dataSourceId, dataSourceType).toPromise();
  }

  async saveEditConnectorStudioTemplate(userId: number, templateId: number, 
    templateData: { warehouseId?: number, destinationType?: string, sessionState: string }, 
    isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      // Use existing legacy API
      return this.saveTemplateData(templateId, []);
    }
    
    return this.templateSourceApi.saveEditedConnectorStudioTemplate(templateData, templateId, userId).toPromise();
  }

  async saveEditConnectorStudioTemplateLater(userId: number, templateId: number,
    templateData: { warehouseId?: number, destinationType?: string, sessionState: string },
    isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      // Use existing legacy API
      return this.applyTemplateChangesLater(templateId, [], [], userId, {});
    }

    return this.templateSourceApi.saveEditedConnectorStudioTemplateLater(templateData, templateId, userId).toPromise();
  }

  async deleteTemplateNew(userId: number, templateId: number, isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      // Use existing legacy API
      return this.deleteTemplate(userId, templateId);
    }

    return this.templateSourceApi.deleteTemplate(templateId, userId).toPromise();
  }
// NOT USED - FOR FUTURE REFERENCE
  async getTemplateNew(userId: number, templateId: number, isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      // Use existing legacy API
      return this.getTemplate(userId, templateId);
    }

    return this.templateSourceApi.getTemplate(templateId, userId).toPromise();
  }

  async getAllTemplates(userId: number, isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      // Use existing legacy API
      return this.getTemplateList(userId);
    }

    return this.templateSourceApi.getAllTemplates(userId).toPromise();
  }

  async getTemplatesByType(userId: number, type: string, isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      // Use existing legacy API - filter by type client-side
      const templates = await this.getTemplateList(userId);
      return templates?.filter(template => template.type === type) || [];
    }

    return this.templateSourceApi.getTemplatesByType(userId, type).toPromise();
  }

  async getTemplateNames(userId: number, isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      // Use existing legacy API
      return this.getOrgTemplateNames(userId);
    }

    return this.templateSourceApi.getTemplateNames(userId).toPromise();
  }

  async getActiveTemplateIntegrations(templateId: number, userId: number, isLegacy: boolean = false): Promise<any> {
    if (isLegacy) {
      // Use existing legacy API
      return this.getActiveIntegrations(templateId);
    }

    return this.templateSourceApi.getActiveTemplateIntegrations(templateId, userId).toPromise();
  }
}
